import React, {SyntheticEvent} from 'react';
import {compose} from 'recompose';
import block from 'bem-cn-lite';
import {Suggest, Icon} from '@yandex-data-ui/common';
import {HTML} from '@doc-tools/components';

import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

import {BreadcrumbItem, SearchItemType} from 'models/search';
import {InnerProps} from 'utils/common';
import {default as routerInstance} from 'router';
import ButtonWithIcon from 'components/Buttons/ButtonWithIcon/ButtonWithIcon';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import {ServiceIcon} from 'components/ServiceIcon';
import {searchForSuggest} from 'units/search/api';

import rightIcon from 'icons/chevron-right-small.svg';
import clearIcon from 'icons/clear.svg';

import './SearchSuggest.scss';

const b = block('SearchSuggest');

function getSearchLink(baseLink: string, query: string) {
    return `${baseLink}?q=${query}`;
}

enum SuggestItemType {
    Text = 'text',
    Page = 'page',
    Service = 'service',
    Marketplace = 'marketplace',
    Group = 'group',
    Delimiter = 'delimiter',
    Link = 'link',
}

interface SearchSuggestBaseItem {
    type: SuggestItemType;
    disabled?: boolean;
}

interface SearchSuggestTextItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Text;
    title: string;
}

interface SearchSuggestPageItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Page;
    title: string;
    link: string;
    description?: string;
    breadcrumbs?: BreadcrumbItem[];
}

interface SearchSuggestServiceItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Service;
    title: string;
    link: string;
    icon: string;
    description?: string;
}

interface SearchSuggestMarketplaceItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Marketplace;
    title: string;
    link: string;
    icon?: string;
    description?: string;
}

interface SearchSuggestGroupItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Group;
    title: string;
}

interface SearchSuggestDelimiterItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Delimiter;
}

interface SearchSuggestLinkItem extends SearchSuggestBaseItem {
    type: SuggestItemType.Link;
    title: string;
    link: string;
}

type SearchSuggestLinkableItem =
    | SearchSuggestPageItem
    | SearchSuggestServiceItem
    | SearchSuggestMarketplaceItem
    | SearchSuggestLinkItem;

type SearchSuggestItem =
    | SearchSuggestLinkableItem
    | SearchSuggestTextItem
    | SearchSuggestGroupItem
    | SearchSuggestDelimiterItem;

interface SearchSuggestDefaultProps {
    searchLink: string;
}

function isLinkableItem(item: SearchSuggestItem): item is SearchSuggestLinkableItem {
    return (
        item.type === SuggestItemType.Page ||
        item.type === SuggestItemType.Service ||
        item.type === SuggestItemType.Marketplace ||
        item.type === SuggestItemType.Link
    );
}

export interface SearchSuggestProps extends Partial<SearchSuggestDefaultProps> {
    query?: string;
    placeholder?: string;
    searchLink?: string;
    focused?: boolean;
    containerClass?: string;
    className?: string;
    onFocus?: () => void;
}

type SearchSuggestInnerProps = InnerProps<SearchSuggestProps, SearchSuggestDefaultProps> &
    WithLocaleProps &
    WithTranslationProps;

class SearchSuggest extends React.Component<SearchSuggestInnerProps> {
    static defaultProps = {
        searchLink: '/search',
    };

    state = {
        query: this.props.query || '',
    };

    private textInputRef = React.createRef<Suggest<SearchSuggestItem>>();

    componentDidUpdate(prevProps: SearchSuggestProps) {
        if (this.props.query !== undefined && prevProps.query !== this.props.query) {
            this.setState({query: this.props.query});
        }

        if (this.props.focused && !prevProps.focused) {
            setTimeout(() => {
                this.textInputRef.current?.textInputRef.current?.focus();
            }, 0);
        }
    }

    render() {
        const {className, placeholder, containerClass, focused, onFocus} = this.props;
        const {query} = this.state;

        return (
            <div className={b('wrapper', containerClass)} onFocus={onFocus}>
                <Suggest
                    className={b(null, className)}
                    popupClassName={b('popup')}
                    text={query}
                    onUpdate={(value) => this.setState({query: value})}
                    getItems={this.getItems}
                    onItemClick={this.handleItemClick}
                    renderItem={this.renderItem}
                    size="l"
                    debounce={300}
                    applicableInputValue={true}
                    onInputEnterKeyDown={this.handleEnterKeyDown}
                    placeholder={placeholder}
                    autoFocus={focused}
                    ref={this.textInputRef}
                />
                {/* CLOUDFRONT-3801 */}
                {query && (
                    <ButtonWithIcon
                        className={b('clear-button')}
                        onClick={this.clearQuery}
                        icon={clearIcon}
                        iconSize={12}
                        size="xs"
                    />
                )}
            </div>
        );
    }

    private renderItem = (item: SearchSuggestItem) => {
        let content;

        switch (item.type) {
            case SuggestItemType.Delimiter:
                content = null;
                break;
            case SuggestItemType.Link:
                content = (
                    <React.Fragment>
                        <span>{item.title}</span>
                        <Icon data={rightIcon} size={10} />
                    </React.Fragment>
                );
                break;
            case SuggestItemType.Service:
            case SuggestItemType.Marketplace:
                content = (
                    <React.Fragment>
                        {item.icon && <div className={b('item-logo')}>{this.renderIcon(item)}</div>}
                        <div className={b('item-text')}>
                            <h5 className={b('item-title')}>
                                <HTML>{item.title}</HTML>
                            </h5>
                            {item.description && (
                                <div className={b('item-description')}>
                                    <HTML>{item.description}</HTML>
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                );
                break;
            case SuggestItemType.Group:
                content = (
                    <h5>
                        <HTML>{item.title}</HTML>
                    </h5>
                );
                break;
            case SuggestItemType.Page:
                content = (
                    <div>
                        <span className={b('item-title')}>
                            <HTML>{item.title}</HTML>
                        </span>
                        {item.breadcrumbs ? (
                            <Breadcrumbs
                                items={item.breadcrumbs}
                                depth={2}
                                delimiter="arrow"
                                className={b('item-breadcrumbs')}
                            />
                        ) : (
                            <div className={b('item-description')}>
                                <HTML>{item.description}</HTML>
                            </div>
                        )}
                    </div>
                );
                break;
            default:
                content = <HTML>{item.title}</HTML>;
                break;
        }

        return <div className={b('item', {type: item.type})}>{content}</div>;
    };

    private renderIcon = (item: SearchSuggestItem) => {
        switch (item.type) {
            case SuggestItemType.Service:
                return <ServiceIcon name={item.icon} size={16} className={b('item-icon')} />;
            case SuggestItemType.Marketplace:
                return item.icon && <img src={item.icon} className={b('item-icon')} />;
            default:
                return null;
        }
    };

    private getItems = async (query: string): Promise<SearchSuggestItem[]> => {
        const {searchLink, i18nK} = this.props;

        try {
            const data = await this.fetch(query);

            // group items
            const items = data?.map.reduce<SearchSuggestItem[]>((acc, {type}) => {
                if (acc.length > 0) {
                    acc.push({
                        type: SuggestItemType.Delimiter,
                        disabled: true,
                    });
                }

                acc.push({
                    type: SuggestItemType.Group,
                    title: i18nK(`search-item_type-${type}`, {
                        default: i18nK('search-item_type-main'),
                    }),
                    disabled: true,
                });
                const groupItems = data.hits
                    .filter((item) => item.type === type)
                    .map<
                        | SearchSuggestPageItem
                        | SearchSuggestServiceItem
                        | SearchSuggestMarketplaceItem
                    >((item) => {
                        const title = item.highlights?.title || item.title;
                        const description = item.highlights?.description || item.description;

                        switch (type) {
                            case SearchItemType.Services:
                                return {
                                    type: SuggestItemType.Service,
                                    title,
                                    description,
                                    link: item.url,
                                    icon: item.name,
                                };
                            case SearchItemType.Marketplace:
                                return {
                                    type: SuggestItemType.Marketplace,
                                    title,
                                    description,
                                    link: item.url,
                                    icon: item.icon,
                                };
                            default: {
                                const breadcrumbs = item.breadcrumbs?.map(({name}) => ({name}));
                                if (breadcrumbs && item.subType) {
                                    breadcrumbs.unshift({name: item.subType});
                                }

                                return {
                                    type: SuggestItemType.Page,
                                    title,
                                    description,
                                    link: item.url,
                                    breadcrumbs,
                                };
                            }
                        }
                    });
                const newAcc = acc.concat(groupItems);

                return newAcc;
            }, []);

            // add link to all results
            if (items && items.length > 0) {
                items.push({
                    type: SuggestItemType.Link,
                    title: i18nK('search_all-results'),
                    link: getSearchLink(searchLink, query),
                });
            }

            return items ?? [];
        } catch (error) {
            return [
                {
                    type: SuggestItemType.Text,
                    title: i18nK('search_error-text'),
                    disabled: true,
                },
            ];
        }
    };

    private handleItemClick = (item: SearchSuggestItem) => {
        if (!item.disabled && isLinkableItem(item)) {
            this.selectItem(item.link);
        }
    };

    private handleEnterKeyDown = (query: string) => {
        this.selectItem(getSearchLink(this.props.searchLink, query));
    };

    private selectItem = (link: string) => {
        //@ts-ignore TODO: types for router
        routerInstance.push(link);
        this.setState({query: ''});
    };

    private fetch = async (query: string) => {
        const {locale} = this.props;
        return query ? searchForSuggest(locale, query) : null;
    };

    private clearQuery = (e: SyntheticEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        this.setState({query: ''});
    };
}

export default compose<SearchSuggestInnerProps, SearchSuggestProps>(
    withLocale,
    withTranslation('search'),
)(SearchSuggest);
